// v4.07 - AUDIT-12: Inline confidence badge chip on NLP answer messages - 2026-04-26
// v3.83 - NLP-02: Boundary warning cards + general knowledge disclaimer + Ask Anyway path - 2026-03-15
// v3.77 - SHOW-01: Updated ShowYourWork to ai_traces model (entityType/entityId) - 2026-03-13
// v3.60 - NLP Chat Widget - 2026-03-03
//
// Floating chat bubble that opens a slide-up panel for natural-language
// data queries. Powered by the NLP Orchestrator in pipeline-runner.
//
// Features:
//   - Floating FAB in bottom-right corner
//   - Slide-up chat drawer (400px wide on desktop, full-width on mobile)
//   - User messages + assistant responses with citations
//   - Clarifier UI (single/multi-select chips)
//   - Thumbs up/down feedback per message
//   - "New conversation" reset button
//   - Rate-limit and error messages displayed inline

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Fab,
  Drawer,
  Typography,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

import Alert from '@mui/material/Alert';
import { useChat, ChatMessage } from '../hooks/useChat';
import { nlpChatService } from '../services/api';
import ShowYourWork from './ShowYourWork';
import { DataDisclaimerBanner } from './DataDisclaimerBanner'; // v3.94 - MISSED-ID-04 EDGS

// ---------------------------------------------------------------------------
// v4.07 - AUDIT-12: Confidence badge maps (mirrors ShowYourWork.tsx LABEL_COLOR)
// ---------------------------------------------------------------------------

const CONFIDENCE_BADGE_COLOR: Record<string, string> = {
  'Strong signal':                       '#4CAF50',
  'Good indication':                     '#2196F3',
  'Early signal':                        '#FF9800',
  'Early signal \u2014 verify recommended': '#FF9800',
};

const CONFIDENCE_BADGE_LABEL: Record<string, string> = {
  'Strong signal':                       'High confidence',
  'Good indication':                     'Good confidence',
  'Early signal':                        'Verify recommended',
  'Early signal \u2014 verify recommended': 'Verify recommended',
};

// ---------------------------------------------------------------------------
// Citation chip component
// ---------------------------------------------------------------------------

interface CitationChipProps {
  claim: string;
  sourceTable: string;
  sourceColumn: string;
  sourceValue: string;
}

const CitationChip: React.FC<CitationChipProps> = ({ claim, sourceTable, sourceColumn, sourceValue }) => (
  <Tooltip
    title={`${sourceTable}.${sourceColumn} = ${sourceValue}`}
    placement="top"
    arrow
  >
    <Chip
      label={claim}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.68rem', cursor: 'default', maxWidth: 200 }}
    />
  </Tooltip>
);

// ---------------------------------------------------------------------------
// Single message bubble
// ---------------------------------------------------------------------------

interface MessageBubbleProps {
  message: ChatMessage;
  onFeedback: (messageId: string, rating: number) => void;
  feedbackGiven: Record<string, number>;
  sessionId?: string;
  messageIndex: number;
  // v3.83 - NLP-02: boundary "Ask Anyway" callback
  onAskAnyway: (originalText: string) => void;
}

// v PORTAL-002: Strip em dashes from AI-generated text before rendering.
// LANG-01 prohibits em dashes in prompts but they can still appear in model output.
// Replace with comma+space which is grammatically safe for all em dash contexts.
function sanitizeContent(text: string): string {
  return text.replace(/\u2014/g, ', ');
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onFeedback,
  feedbackGiven,
  onAskAnyway,
}) => {
  const theme = useTheme();
  const isUser = message.role === 'user';
  const isError = message.role === 'error';
  const isBoundary = message.role === 'boundary';
  const given = feedbackGiven[message.id];

  // v3.83 - NLP-02: Boundary warning card (WARN or REJECT)
  if (isBoundary) {
    const isReject = message.boundary_level === 'REJECT';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1.5 }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1.25,
            maxWidth: '90%',
            borderRadius: '12px',
            border: `1px solid ${isReject ? theme.palette.warning.main : theme.palette.info.main}`,
            bgcolor: isReject
              ? (theme.palette.mode === 'dark' ? 'rgba(237,108,2,0.12)' : '#fff8f0')
              : (theme.palette.mode === 'dark' ? 'rgba(2,136,209,0.10)' : '#f0f7ff'),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            {isReject ? (
              <BlockOutlinedIcon sx={{ fontSize: 18, color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
            ) : (
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main', mt: 0.25, flexShrink: 0 }} />
            )}
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {message.content}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" gap={0.5}>
            <Button
              size="small"
              variant="outlined"
              color={isReject ? 'warning' : 'info'}
              onClick={() => message.original_text && onAskAnyway(message.original_text)}
              sx={{ fontSize: '0.72rem', py: 0.25, textTransform: 'none', borderRadius: 2 }}
            >
              Ask Anyway
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      {/* v3.94 - MISSED-ID-04 EDGS Tier 2: HIGH-risk blocking reveal */}
      {!isUser && !isError && message.risk_level === 'HIGH' ? (
        <DataDisclaimerBanner disclaimerText={message.disclaimer_text || ''}>
          <Paper
            elevation={0}
            sx={{
              px: 1.5,
              py: 1,
              maxWidth: '85%',
              borderRadius: '16px 16px 16px 4px',
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
              color: theme.palette.text.primary,
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {sanitizeContent(message.content)}
            </Typography>
          </Paper>
        </DataDisclaimerBanner>
      ) : (
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1,
            maxWidth: '85%',
            borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            bgcolor: isError
              ? theme.palette.error.light
              : isUser
              ? theme.palette.primary.main
              : theme.palette.mode === 'dark'
              ? theme.palette.grey[800]
              : theme.palette.grey[100],
            color: isError
              ? theme.palette.error.contrastText
              : isUser
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {isUser ? message.content : sanitizeContent(message.content)}
          </Typography>
        </Paper>
      )}
      {/* v3.94 - MISSED-ID-04 EDGS Tier 1: MEDIUM-risk inline alert */}
      {!isUser && !isError && message.risk_level === 'MEDIUM' && message.disclaimer_text && (
        <Alert severity="warning" sx={{ mt: 0.75, maxWidth: '85%', py: 0.25, fontSize: '0.75rem' }}>
          {message.disclaimer_text}
        </Alert>
      )}

      {/* Citations */}
      {message.citations && message.citations.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5} maxWidth="85%">
          {message.citations.map((c, i) => (
            <CitationChip
              key={i}
              claim={c.claim}
              sourceTable={c.source_table}
              sourceColumn={c.source_column}
              sourceValue={c.source_value}
            />
          ))}
        </Stack>
      )}

      {/* v3.83 - NLP-02: General knowledge disclaimer */}
      {!isUser && !isError && message.is_general_knowledge && message.disclaimer && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5} maxWidth="85%">
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
            {message.disclaimer}
          </Typography>
        </Stack>
      )}

      {/* v4.04 - STORY-171: Answer consistency change notice */}
      {!isUser && !isError && message.answer_changed && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          This answer has changed since your last session
          {message.divergence_pct != null ? ` (${message.divergence_pct.toFixed(1)}% difference)` : ''}.
        </Typography>
      )}

      {/* v4.07 - AUDIT-12: Inline confidence badge — visible without clicking ShowYourWork */}
      {!isUser && !isError && message.confidence_label && CONFIDENCE_BADGE_COLOR[message.confidence_label] && (
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label={CONFIDENCE_BADGE_LABEL[message.confidence_label] ?? message.confidence_label}
            size="small"
            sx={{
              bgcolor: CONFIDENCE_BADGE_COLOR[message.confidence_label],
              color: '#fff',
              fontSize: '0.68rem',
              height: 20,
              fontWeight: 500,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      )}

      {/* v3.77 - SHOW-01: Show Your Work panel — only when trace entity ID is present */}
      {!isUser && !isError && message.message_id && (
        <ShowYourWork
          entityType="nlp_answer"
          entityId={message.message_id}
        />
      )}

      {/* Feedback row for assistant messages */}
      {!isUser && !isError && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
          {message.cache_hit && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              cached
            </Typography>
          )}
          {message.latency_ms && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              {message.latency_ms < 1000
                ? `${message.latency_ms}ms`
                : `${(message.latency_ms / 1000).toFixed(1)}s`}
            </Typography>
          )}
          <Tooltip title="Helpful">
            <IconButton
              size="small"
              onClick={() => onFeedback(message.id, 5)}
              sx={{ p: 0.25, opacity: given === 5 ? 1 : 0.4 }}
            >
              {given === 5 ? (
                <ThumbUpIcon sx={{ fontSize: 14 }} color="primary" />
              ) : (
                <ThumbUpOutlinedIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Not helpful">
            <IconButton
              size="small"
              onClick={() => onFeedback(message.id, 1)}
              sx={{ p: 0.25, opacity: given === 1 ? 1 : 0.4 }}
            >
              {given === 1 ? (
                <ThumbDownIcon sx={{ fontSize: 14 }} color="error" />
              ) : (
                <ThumbDownOutlinedIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main ChatWidget
// ---------------------------------------------------------------------------

const WIDGET_WIDTH = 380;

const ChatWidget: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, number>>({});
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { messages, isLoading, clarifiers, sendMessage, askAnyway, selectClarifier, submitClarifiers, reset } =
    useChat();

  // Session ID is managed inside useChat via sessionStorage
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sessionIdRef = useRef<string | undefined>(
    sessionStorage.getItem('nlp_session_id') || undefined
  );

  // v3.60 - Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, clarifiers]);

  // v3.60 - Focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    setInputValue('');
    await sendMessage(text);
  }, [inputValue, isLoading, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // v3.60 - Submit thumbs feedback to pipeline-runner
  const handleFeedback = useCallback(
    async (messageId: string, rating: number) => {
      if (feedbackGiven[messageId]) return; // already rated
      setFeedbackGiven(prev => ({ ...prev, [messageId]: rating }));

      const msgIndex = messages.findIndex(m => m.id === messageId);
      const sid = sessionStorage.getItem('nlp_session_id');
      if (!sid || msgIndex < 0) return;

      try {
        await nlpChatService.submitFeedback({
          session_id: sid,
          message_index: msgIndex,
          rating,
        });
      } catch {
        // Feedback failures are non-fatal — don't surface to user
      }
    },
    [feedbackGiven, messages]
  );

  const handleReset = useCallback(() => {
    reset();
    setFeedbackGiven({});
    setInputValue('');
  }, [reset]);

  return (
    <>
      {/* Floating action button */}
      <Fab
        color="primary"
        aria-label="Open analytics chat"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: theme.zIndex.speedDial,
          boxShadow: 4,
        }}
      >
        <ChatIcon />
      </Fab>

      {/* Chat drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100vw' : WIDGET_WIDTH,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
        // v3.60 - Don't block the rest of the page
        ModalProps={{ keepMounted: true }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
            Ask your data
          </Typography>
          <Tooltip title="New conversation">
            <IconButton size="small" onClick={handleReset} sx={{ color: 'inherit', opacity: 0.8 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Message list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 1.5,
          }}
        >
          {messages.length === 0 && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.5,
                gap: 1,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 40 }} />
              <Typography variant="body2" textAlign="center" color="text.secondary">
                Ask a question about your data.
                <br />
                <em>e.g. "What was my revenue last quarter?"</em>
              </Typography>
            </Box>
          )}

          {messages.map((msg, idx) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              messageIndex={idx}
              onFeedback={handleFeedback}
              feedbackGiven={feedbackGiven}
              onAskAnyway={askAnyway}
            />
          ))}

          {/* Clarifier UI */}
          {clarifiers && (
            <Box sx={{ mb: 1.5 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: '16px 16px 16px 4px',
                  bgcolor:
                    theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                  mb: 1,
                }}
              >
                <Typography variant="body2">{clarifiers.question}</Typography>
              </Paper>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {clarifiers.options.map(opt => (
                  <Chip
                    key={opt}
                    label={opt}
                    size="small"
                    clickable
                    color={clarifiers.selected.includes(opt) ? 'primary' : 'default'}
                    variant={clarifiers.selected.includes(opt) ? 'filled' : 'outlined'}
                    onClick={() => selectClarifier(opt)}
                  />
                ))}
              </Stack>
              {clarifiers.selected.length > 0 && (
                <Box mt={1}>
                  <Chip
                    label="Send"
                    color="primary"
                    size="small"
                    onClick={submitClarifiers}
                    disabled={isLoading}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Typing indicator */}
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Analyzing…
              </Typography>
            </Box>
          )}

          <div ref={bottomRef} />
        </Box>

        <Divider />

        {/* Input row */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Ask about your data…"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <Tooltip title="Send (Enter)">
            <span>
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={isLoading || !inputValue.trim()}
                sx={{ mb: 0.25 }}
              >
                {isLoading ? <CircularProgress size={20} /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Drawer>
    </>
  );
};

export default ChatWidget;
